import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";

import {
  getMyCoachMemoryProfile,
  addMyCoachMemoryPreference,
  deleteMyCoachMemoryPreference,
} from "../queries/userMemory";

// Inline memory view embedded inside HistoryDrawer when the Memory tab
// is active. Sourced from /api/users/me/coach-memory/profile (reflect
// cache + Redis prefs hash; no Hindsight roundtrip). View-only for
// everything except the user-set preferences section.

const Section = ({ title, count, children, empty }) => (
  <div className="memory-profile-section">
    <div className="memory-profile-section-header">
      <span className="memory-profile-section-title">{title}</span>
      {typeof count === "number" && (
        <span className="memory-profile-section-count">{count}</span>
      )}
    </div>
    {count === 0 || !children ? (
      <div className="memory-profile-empty">{empty}</div>
    ) : (
      <div className="memory-profile-section-body">{children}</div>
    )}
  </div>
);

const MemoryProfileDrawer = observer(({ userStore }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    if (!userStore?.authData) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getMyCoachMemoryProfile(userStore.authData);
      setProfile(data);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  const handleAddPreference = async () => {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const record = await addMyCoachMemoryPreference(userStore.authData, text);
      setProfile(p => p ? { ...p, user_preferences: [...(p.user_preferences || []), record] } : p);
      setDraft("");
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePreference = async (id) => {
    try {
      await deleteMyCoachMemoryPreference(userStore.authData, id);
      setProfile(p => p ? { ...p, user_preferences: (p.user_preferences || []).filter(x => x.id !== id) } : p);
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  const reflect = profile?.reflect || {};
  const userPrefs = profile?.user_preferences || [];

  return (
    <div className="memory-profile-inline" role="region" aria-label="Memory profile">
      <div className="memory-profile-inline-toolbar">
        <button
          className="memory-profile-icon-btn"
          title="Refresh"
          onClick={refresh}
          disabled={loading}
        >↻</button>
      </div>

      {error && <div className="memory-profile-error">{error}</div>}
      {loading && !profile && <div className="memory-profile-loading">Loading…</div>}

      {profile && (
        <div className="memory-profile-body">
          <Section title="Topics" count={reflect.topics?.length || 0} empty="None yet.">
            {(reflect.topics || []).map((t, i) => (
              <div key={i} className="memory-profile-item">{t}</div>
            ))}
          </Section>

          <Section title="Threads" count={reflect.threads?.length || 0} empty="No active threads.">
            {(reflect.threads || []).map((t, i) => {
              const anchor = t.anchor || t.name || "(unnamed)";
              const summary = t.summary || "";
              const last = t.last_active || t.last_active_at || "";
              return (
                <div key={i} className="memory-profile-thread">
                  <div className="memory-profile-thread-anchor">{anchor}</div>
                  {summary && <div className="memory-profile-thread-summary">{summary}</div>}
                  {last && <div className="memory-profile-thread-meta">last active: {last}</div>}
                </div>
              );
            })}
          </Section>

          <Section title="Entities" count={reflect.entities?.length || 0} empty="None yet.">
            <div className="memory-profile-entities">
              {(reflect.entities || []).map((e, i) => {
                const name = e.name || e.plex_id || "(unnamed)";
                const cat = e.category || "";
                const pid = e.plex_id || "";
                return (
                  <span key={i} className="memory-profile-entity-chip" title={pid}>
                    {name}{cat ? <span className="memory-profile-entity-cat"> · {cat}</span> : null}
                  </span>
                );
              })}
            </div>
          </Section>

          <Section title="Decisions" count={reflect.decisions?.length || 0} empty="No decisions captured.">
            {(reflect.decisions || []).map((d, i) => (
              <div key={i} className="memory-profile-item">{d}</div>
            ))}
          </Section>

          <Section
            title="Preferences"
            count={(userPrefs.length || 0) + (reflect.preferences?.length || 0)}
            empty="No preferences yet."
          >
            {userPrefs.length > 0 && (
              <div className="memory-profile-prefs-group">
                <div className="memory-profile-prefs-label">Set by you</div>
                {userPrefs.map(p => (
                  <div key={p.id} className="memory-profile-pref-row">
                    <div className="memory-profile-pref-text">{p.text}</div>
                    <button
                      className="memory-profile-pref-delete"
                      onClick={() => handleDeletePreference(p.id)}
                      title="Delete"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
            {(reflect.preferences || []).length > 0 && (
              <div className="memory-profile-prefs-group">
                <div className="memory-profile-prefs-label">Inferred from conversation</div>
                {reflect.preferences.map((p, i) => (
                  <div key={i} className="memory-profile-pref-row memory-profile-pref-inferred">
                    <div className="memory-profile-pref-text">{p}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="memory-profile-pref-add">
              <textarea
                className="memory-profile-pref-input"
                placeholder="Add a preference (e.g. always anchor on a target before searching)…"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddPreference();
                  }
                }}
                rows={2}
                maxLength={500}
                disabled={submitting}
              />
              <button
                className="memory-profile-pref-add-btn"
                onClick={handleAddPreference}
                disabled={submitting || !draft.trim()}
              >Add</button>
            </div>
          </Section>

          <div className="memory-profile-footer">
            {profile.bank_id_prefix && <span>bank {profile.bank_id_prefix}</span>}
            {profile.reflect_latency_ms ? (
              <span> · last reflect {profile.reflect_latency_ms}ms</span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
});

export default MemoryProfileDrawer;
